import { useEffect, useRef } from "react";
import { X } from "lucide-react";

// Helper to detect YouTube URL and extract video ID
const getYouTubeId = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:embed\/|watch\?v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : null;
};

// Helper to detect Rutube URL
const isRutubeUrl = (url) => {
  if (!url) return false;
  return url.includes('rutube.ru');
};

// Get Rutube embed URL
const getRutubeEmbedUrl = (url) => {
  if (!url) return null;
  // If already an embed URL, return as is
  if (url.includes('/play/embed/')) {
    return url;
  }
  // Convert video URL to embed URL
  // https://rutube.ru/video/VIDEO_ID/ -> https://rutube.ru/play/embed/VIDEO_ID
  const match = url.match(/rutube\.ru\/video\/(?:private\/)?([a-zA-Z0-9]+)/);
  if (match) {
    // Preserve query params for private videos
    const queryMatch = url.match(/\?(.+)$/);
    const query = queryMatch ? `?${queryMatch[1]}` : '';
    return `https://rutube.ru/play/embed/${match[1]}${query}`;
  }
  return url;
};

export const VideoModal = ({ videoUrl, isOpen, onClose, poster, label = "Видео о курсе" }) => {
  const videoRef = useRef(null);
  const dialogRef = useRef(null);
  const youtubeId = getYouTubeId(videoUrl);
  const isYouTube = !!youtubeId;
  const isRutube = isRutubeUrl(videoUrl);
  const rutubeEmbedUrl = isRutube ? getRutubeEmbedUrl(videoUrl) : null;

  useEffect(() => {
    if (!isOpen) return;
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    const dialog = dialogRef.current;
    dialog?.querySelector('button')?.focus();
    document.body.style.overflow = "hidden";
    const handleKey = e => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); }
      if (e.key !== "Tab") return;
      const items = [...dialog.querySelectorAll('button,a[href],iframe,video[controls]')];
      const first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", handleKey);
    return () => { document.removeEventListener("keydown", handleKey); document.body.style.overflow = overflow; previous?.focus(); };
  }, [isOpen, onClose]);

  // Pause video when modal closes (only for non-YouTube/Rutube)
  useEffect(() => {
    if (!isOpen && videoRef.current && !isYouTube && !isRutube) {
      videoRef.current.pause();
    }
  }, [isOpen, isYouTube, isRutube]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center"
      ref={dialogRef} role="dialog" aria-modal="true" aria-label={label}
      data-testid="video-modal"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/90 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        aria-label="Закрыть видео" data-testid="video-modal-close"
      >
        <X className="w-8 h-8 text-white" />
      </button>

      {/* Video Container */}
      <div className="relative z-10 w-full max-w-5xl mx-4">
        <div className="relative aspect-video bg-black rounded-xl overflow-hidden shadow-2xl">
          {isYouTube ? (
            <iframe
              src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0`}
              className="w-full h-full"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
              data-testid="youtube-player"
            />
          ) : isRutube ? (
            <iframe
              src={rutubeEmbedUrl}
              className="w-full h-full"
              title="Rutube video player"
              frameBorder="0"
              allow="clipboard-write; autoplay"
              allowFullScreen
              data-testid="rutube-player"
            />
          ) : (
            <video
              ref={videoRef}
              src={videoUrl}
              poster={poster}
              preload="metadata"
              aria-label={label}
              className="w-full h-full"
              controls
              autoPlay
              playsInline
              data-testid="video-player"
            >
              Ваш браузер не поддерживает воспроизведение видео.
            </video>
          )}
        </div>
        <a className="video-fallback" href={isRutube ? videoUrl.replace('/play/embed/', '/video/') : isYouTube ? `https://www.youtube.com/watch?v=${youtubeId}` : videoUrl} target="_blank" rel="noopener noreferrer">Видео не загружается? Открыть в отдельной вкладке ↗</a>
      </div>
    </div>
  );
};

export default VideoModal;
